import dayjs from '../../../../shared/services/dayjs.js';

/**
 * Generisches Hover-Popup (z.B. für Kamerabilder), das direkt unterhalb des auslösenden Elements
 * innerhalb des umgebenden .main-container erscheint. Wird von Tagesübersicht und Mitarbeiter > Zeiten geteilt.
 * - lädt Inhalte debounced nach (verhindert unnötige Anfragen beim schnellen Überfahren mehrerer Icons)
 * - verwirft verspätet ankommende Antworten eines vorherigen Hovers (Stale-Response-Schutz)
 * - bleibt sichtbar, solange der User im Popup selbst hovert
 */
class HoverPopupService {
    constructor(popupElementId) {
        this.popupElementId = popupElementId;
        this.hideTimeout = null;
        this.hoverToken = 0;
        this.popupMitHoverGebunden = null;
    }

    _getPopup() {
        return document.getElementById(this.popupElementId);
    }

    /**
     * Bindet die Hover-Cancel-Listener auf das Popup-Element selbst. Das Popup-Element wird neu erzeugt,
     * sobald die umgebende View neu geladen wird (fetchToHtml ersetzt den #main Inhalt), der Service
     * selbst ist aber ein module-level Singleton und überlebt das. Daher an der Element-Identität statt
     * an einem einmaligen Flag festmachen, sonst bleiben die Listener nach einem Reload des DOM-Knotens
     * fehlend und das Popup verschwindet beim Versuch, es zu erreichen.
     */
    _initPopupHoverEinmalig(popup) {
        if (this.popupMitHoverGebunden === popup) return;
        this.popupMitHoverGebunden = popup;
        popup.addEventListener('mouseenter', () => clearTimeout(this.hideTimeout));
        popup.addEventListener('mouseleave', () => this._planeAusblenden(popup));
    }

    _planeAusblenden(popup, delay = 400) {
        this.hideTimeout = setTimeout(() => {
            popup.style.visibility = 'hidden';
        }, delay);
    }

    /**
     * Dockt das Popup direkt unterhalb des Trigger-Elements an (statt an der Mausposition), damit der
     * Weg vom Trigger zum Popup immer kurz und vorhersehbar ist (leichter zu erreichen, bevor es
     * ausgeblendet wird).
     * @param {HTMLElement} popup
     * @param {HTMLElement} triggerElement
     */
    _positioniere(popup, triggerElement) {
        // Fallback, falls kein .main-container Vorfahre existiert (z.B. innerhalb eines Tabs), damit
        // die Positionierung nie den Rest des Hover-Handlers (Anzeige/Laden der Inhalte) blockiert.
        const container = popup.closest('.main-container') || popup.parentElement || document.body;
        const containerRect = container.getBoundingClientRect();
        const containerX = containerRect.left + window.scrollX;
        const containerY = containerRect.top + window.scrollY;
        const triggerRect = triggerElement.getBoundingClientRect();
        let left = triggerRect.left + window.scrollX - containerX;
        let top = triggerRect.bottom + window.scrollY - containerY + 4;
        const maxLeft = containerRect.width - popup.offsetWidth;
        const maxTop = containerRect.height - popup.offsetHeight;
        left = Math.min(Math.max(0, left), maxLeft);
        top = Math.min(Math.max(0, top), maxTop);
        popup.style.top = `${top}px`;
        popup.style.left = `${left}px`;
    }

    /**
     * Registriert Hover-Events für ein Icon: zeigt das Popup direkt unterhalb des Icons an, lädt Inhalte
     * debounced nach und blendet das Popup beim Verlassen wieder aus.
     * @param {HTMLElement} icon Element, das das Popup per Hover auslöst
     * @param {() => Promise<any>} ladeInhalt liefert die anzuzeigenden Daten
     * @param {(popup: HTMLElement, daten: any) => void} renderInhalt rendert die geladenen Daten in das Popup
     * @param {number} delay Debounce-Verzögerung in ms
     * @param {() => boolean} canShow synchrone Vorprüfung: Popup wird nur angezeigt, falls true (z.B. um
     *   bei bereits bestätigten Schichten gar nicht erst den Ladekreis aufblitzen zu lassen)
     */
    addHoverEvents(icon, ladeInhalt, renderInhalt, delay = 250, canShow = () => true) {
        const popup = this._getPopup();
        this._initPopupHoverEinmalig(popup);
        const positioniereSicher = () => {
            try {
                this._positioniere(popup, icon);
            } catch (error) {
                console.log(error);
            }
        };
        let debounceTimeout;
        icon.addEventListener('mouseenter', () => {
            if (!canShow()) return;
            clearTimeout(this.hideTimeout);
            popup.innerHTML = '<div class="kamera-popup-loading-wrapper"><div class="kamera-popup-loading"></div></div>';
            popup.style.visibility = 'visible';
            positioniereSicher();
            const token = ++this.hoverToken;
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(async () => {
                const daten = await ladeInhalt();
                // Verworfen, falls der User inzwischen ein anderes Icon hovert
                if (token !== this.hoverToken) return;
                popup.innerHTML = '';
                renderInhalt(popup, daten);
                // Erneut positionieren: der Ladekreis war ggf. deutlich kleiner als der finale Inhalt
                // (Bilder + Button), die vorherige Positionierung/Begrenzung war also noch nicht final.
                positioniereSicher();
            }, delay);
        });
        icon.addEventListener('mouseleave', () => {
            clearTimeout(debounceTimeout);
            this._planeAusblenden(popup);
        });
    }

    /** Blendet das Popup sofort aus (z.B. nachdem eine Aktion im Popup abgeschlossen wurde). */
    hide() {
        this._getPopup().style.visibility = 'hidden';
    }
}

/**
 * Rendert eine Liste geladener Kamerabilder (Start/Ende) in ein Hover-Popup-Element.
 * Geteilt zwischen Tagesübersicht und Mitarbeiter > Zeiten.
 * @param {HTMLElement} popup
 * @param {Array<object|null>} geladeneBilder { MimeType, Bild (base64), Typ, Aufnahmedatum }
 */
function renderKamerabilderInPopup(popup, geladeneBilder) {
    const titel = document.createElement('div');
    titel.className = 'popup-titel';
    titel.innerHTML = '<i class="bi bi-camera-fill"></i> Kamerabilder';
    popup.appendChild(titel);
    geladeneBilder.forEach((bild) => {
        if (!bild) return;
        const wrapper = document.createElement('div');
        const img = document.createElement('img');
        img.src = `data:${bild.MimeType};base64,${bild.Bild}`;
        const label = document.createElement('div');
        label.className = 'kamera-popup-bild';
        const beschriftung = bild.Typ === 'Start'
            ? '<i class="bi bi-box-arrow-in-right"></i> Start'
            : '<i class="bi bi-box-arrow-right"></i> Ende';
        label.innerHTML = bild.Aufnahmedatum ? `${beschriftung} · ${dayjs(bild.Aufnahmedatum).tz().format('HH:mm')}` : beschriftung;
        wrapper.appendChild(img);
        wrapper.appendChild(label);
        popup.appendChild(wrapper);
    });
}

export default HoverPopupService;
export { renderKamerabilderInPopup };
